/**
 * Pick a lesson from the library to attach — §2.8 #90.
 *
 * Three behaviours that are easy to lose and each have a reason:
 *
 * - **250ms debounce on the search.** Keeps the request rate sane while typing
 *   without feeling laggy.
 * - **Recent-3 when nothing is typed.** The API returns lessons sorted by
 *   `updated_at` desc, so the top three are almost always what the user wants;
 *   forcing them to type first is a tax on the common case.
 * - **Self-reference is rejected.** A lesson attaching itself is a loop the
 *   backend would happily store.
 *
 * Already-attached lessons are shown and disabled rather than filtered out, so
 * the list does not silently shrink between visits.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, StatusBadge } from '@ai-tutor/ui';
import { BookOpen, Check, Loader2, Search } from 'lucide-react';
import { notify } from '@/lib/notify';
import { lessonApi, type ExtrasAttachment } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { useAddExtrasAttachment } from '@/hooks/lesson/useOnDemandContent';
import { useAutoFocus } from '@/hooks/ui/useAutoFocus';
import { useDebouncedValue } from '@/hooks/ui/useDebouncedValue';

export interface LessonPickerProps {
  lessonId: string;
  existingAttachments: ExtrasAttachment[];
  /**
   * Fired after a successful attach - the modal stays open and shows
   * an inline confirmation. Replaces the old `onDone` (close-on-add)
   * behavior so users can chain multiple attachments without reopening.
   */
  onAdded: (title: string) => void;
}

// How many of the most-recent lessons to surface above the full list
// when the user hasn't typed anything yet. Three's the sweet spot -
// enough to be useful, not so many that it crowds out the search.
const RECENT_LESSONS_COUNT = 3;

export function LessonPicker({
  lessonId,
  existingAttachments,
  onAdded,
}: LessonPickerProps) {
  const [search, setSearch] = useState('');
  // 250ms - keeps the request rate sane while the user types, but still feels
  // live.
  const debouncedSearch = useDebouncedValue(search, 250);

  const searchInputRef = useAutoFocus<HTMLInputElement>();

  const lessonsQuery = useQuery({
    queryKey: queryKeys.lessons.list({
      search: debouncedSearch,
      page: 1,
      folder: 'all',
      includeFolders: false,
      itemType: 'lessons',
    }),
    queryFn: () =>
      lessonApi.getUserLessons({
        search: debouncedSearch || undefined,
        page: 1,
        limit: 20,
        itemType: 'lessons',
      }),
    staleTime: 30_000,
  });

  const addAttachment = useAddExtrasAttachment(lessonId);

  // Build the set of already-attached lesson_ids so we can grey those
  // out - re-attaching the same lesson is a no-op from the user's POV
  // and the backend would happily store duplicates.
  const attachedLessonIds = useMemo(
    () =>
      new Set(
        existingAttachments
          .filter(a => a.kind === 'lesson' && a.lesson_id)
          .map(a => a.lesson_id as string)
      ),
    [existingAttachments]
  );

  const handlePick = async (lessonIdToAttach: string, title: string) => {
    if (lessonIdToAttach === lessonId) {
      notify.error("A lesson can't reference itself");
      return;
    }
    try {
      await addAttachment.mutateAsync({
        kind: 'lesson',
        lesson_id: lessonIdToAttach,
      });
      // Don't close - let the modal handle the "Added X" inline
      // confirmation. Modal state already reflects the new attachment
      // via TanStack Query cache update, so the row will re-render with
      // its "Attached" badge on its own.
      onAdded(title || 'Untitled lesson');
    } catch (err: any) {
      notify.error(err, "Couldn't attach lesson");
    }
  };

  // The API returns `lessons` when `include_folders` isn't passed (our
  // case here - picker is lesson-only). The unified `items` stream is
  // empty in that mode.
  const lessons = lessonsQuery.data?.lessons ?? [];

  // When the user hasn't searched, slice off the top N lessons (the API
  // returns them sorted by updated_at desc) and render them under a
  // "Recent" header. Avoids forcing power users to type when they're
  // attaching one of their last few lessons.
  const showRecent = !debouncedSearch && lessons.length > 0;
  const recentLessons = showRecent
    ? lessons.slice(0, RECENT_LESSONS_COUNT)
    : [];
  const otherLessons = showRecent
    ? lessons.slice(RECENT_LESSONS_COUNT)
    : lessons;

  return (
    <div className='flex h-full flex-col gap-3'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          ref={searchInputRef}
          placeholder='Search your lessons…'
          value={search}
          onChange={e => setSearch(e.target.value)}
          className='bg-transparent pl-10 focus-visible:ring-offset-0'
        />
      </div>

      {/* Shorter cap on a phone, where this sits in a sheet with the keyboard
          possibly up; the list scrolls inside it. */}
      <div className='max-h-[45dvh] flex-1 overflow-y-auto rounded-md border border-border sm:max-h-[480px]'>
        {lessonsQuery.isLoading ? (
          <LessonRowSkeleton />
        ) : lessons.length === 0 ? (
          <div className='py-12 text-center text-sm text-muted-foreground'>
            {debouncedSearch
              ? `No lessons match "${debouncedSearch}".`
              : 'No lessons in your library yet.'}
          </div>
        ) : (
          <div>
            {showRecent && <SectionHeader label='Recent' />}
            <LessonList
              lessons={recentLessons.length > 0 ? recentLessons : otherLessons}
              currentLessonId={lessonId}
              attachedLessonIds={attachedLessonIds}
              isPending={addAttachment.isPending}
              onPick={handlePick}
            />
            {showRecent && otherLessons.length > 0 && (
              <>
                <SectionHeader label='All lessons' />
                <LessonList
                  lessons={otherLessons}
                  currentLessonId={lessonId}
                  attachedLessonIds={attachedLessonIds}
                  isPending={addAttachment.isPending}
                  onPick={handlePick}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionHeader({ label }: { label: string }) {
  return (
    <div className='sticky top-0 z-10 border-b border-border/60 bg-card px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
      {label}
    </div>
  );
}

export function LessonRowSkeleton() {
  // Three placeholder rows match the row height (`py-2.5`) so the list
  // height doesn't shift when real data lands.
  return (
    <div className='divide-y divide-border'>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className='flex items-start gap-3 px-3 py-2.5'>
          <div className='mt-0.5 h-4 w-4 flex-shrink-0 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          <div className='flex-1 space-y-1.5'>
            <div className='h-3.5 w-1/2 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
            <div className='h-3 w-3/4 animate-pulse rounded-sm bg-muted' />
          </div>
        </div>
      ))}
    </div>
  );
}

interface LessonListProps {
  lessons: { lesson_id: string; title: string; description: string }[];
  currentLessonId: string;
  attachedLessonIds: Set<string>;
  isPending: boolean;
  onPick: (lessonId: string, title: string) => void;
}

export function LessonList({
  lessons,
  currentLessonId,
  attachedLessonIds,
  isPending,
  onPick,
}: LessonListProps) {
  return (
    <ul className='divide-y divide-border'>
      {lessons.map(l => {
        const isSelf = l.lesson_id === currentLessonId;
        const isAttached = attachedLessonIds.has(l.lesson_id);
        const disabled = isSelf || isAttached || isPending;
        return (
          <li key={l.lesson_id}>
            <button
              type='button'
              disabled={disabled}
              onClick={() => onPick(l.lesson_id, l.title)}
              className='flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent'
            >
              <BookOpen
                className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                  disabled
                    ? 'text-muted-foreground'
                    : 'text-blue-500 dark:text-primary'
                }`}
              />
              <div className='min-w-0 flex-1'>
                <div
                  className={`truncate text-sm font-medium ${
                    disabled ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {l.title || 'Untitled lesson'}
                </div>
                {l.description && (
                  <div className='mt-0.5 truncate text-xs text-muted-foreground'>
                    {l.description}
                  </div>
                )}
              </div>
              {isSelf && (
                <StatusBadge
                  variant='indigo'
                  size='sm'
                  className='flex-shrink-0'
                >
                  This lesson
                </StatusBadge>
              )}
              {!isSelf && isAttached && (
                <StatusBadge
                  variant='success'
                  size='sm'
                  className='flex-shrink-0'
                >
                  Attached
                </StatusBadge>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
